<template>
  <div class="auth-page" :style="clientColor ? { '--brand-color': clientColor } : null">
    <header class="top-bar">
      <div class="brand-pill" :style="{ backgroundColor: logoBackgroundColor }">
        <img v-if="clientLogo" :src="clientLogo" alt="Client Logo" class="brand-image" />
        <img v-else src="@/../public/static/img/acrew-logo-with-effects-white.png" alt="ACREW Logo" class="brand-image default" />
      </div>
      <div class="top-login">
        <img src="@/../public/static/img/acrew-dash-pattern.png" alt="" class="dash-pattern" />
        <span>Schedule</span>
      </div>
    </header>

    <div class="content-area">
      <div class="card-wrapper">
        <div class="auth-card">

          <!-- Step 1: Choose patient type -->
          <div v-if="currentStep === 1">
            <div class="heading">
              <div class="heading-text">
                <h2>Book an appointment</h2>
              </div>
            </div>
            <p class="step-description">Are you a new or existing patient at {{ clientName }}?</p>
            <div class="button-row button-row--stack">
              <v-btn class="btn-secondary full-width-btn" @click="currentStep = 3">Existing Patient</v-btn>
              <v-btn class="btn-primary full-width-btn" @click="currentStep = 2">New Patient</v-btn>
            </div>
          </div>

          <!-- Step 2: New patient form -->
          <div v-else-if="currentStep === 2">
            <div class="heading">
              <div class="heading-text">
                <h2>New patient</h2>
              </div>
            </div>
            <p class="step-description">Fill in your details to create your appointment.</p>
            <div class="step-container">
              <v-form ref="newPatientForm" class="modern-form" @submit.prevent="checkForm" lazy-validation>
                <div class="form-row form-row--stack-on-mobile">
                  <div class="form-row__col">
                    <v-text-field
                      v-model="form.firstName"
                      :rules="[rules.required, rules.name]"
                      validate-on-blur
                      label="First Name"
                      type="text"
                      required
                      dense
                      hide-details="auto"
                      class="line-input custom-filled" />
                  </div>
                  <div class="form-row__col">
                    <v-text-field
                      v-model="form.lastName"
                      :rules="[rules.required, rules.name]"
                      validate-on-blur
                      label="Last Name"
                      type="text"
                      required
                      dense
                      hide-details="auto"
                      class="line-input custom-filled" />
                  </div>
                </div>
                <v-text-field
                  v-model="form.email"
                  :rules="[rules.required, rules.email]"
                  validate-on-blur
                  label="Email"
                  type="email"
                  required
                  dense
                  hide-details="auto"
                  class="line-input custom-filled" />
                <v-text-field
                  v-model="form.birthDate"
                  :rules="[rules.required]"
                  label="Birth Date"
                  type="date"
                  required
                  dense
                  hide-details="auto"
                  class="line-input custom-filled" />
                <v-text-field
                  v-model="form.phone"
                  :rules="[rules.required, rules.phone]"
                  validate-on-blur
                  label="Phone Number"
                  type="tel"
                  @keyup="numDash"
                  maxlength="12"
                  required
                  dense
                  hide-details="auto"
                  class="line-input custom-filled" />
                <v-text-field
                  v-model="form.address1"
                  :rules="[rules.required]"
                  label="Address Line 1"
                  type="text"
                  required
                  dense
                  hide-details="auto"
                  class="line-input custom-filled" />
                <v-text-field
                  v-model="form.address2"
                  label="Address Line 2"
                  type="text"
                  dense
                  hide-details="auto"
                  class="line-input custom-filled" />
                <div class="form-row form-row--stack-on-mobile">
                  <div class="form-row__col">
                    <v-text-field
                      v-model="form.city"
                      :rules="[rules.required]"
                      label="City"
                      type="text"
                      required
                      dense
                      hide-details="auto"
                      class="line-input custom-filled" />
                  </div>
                  <div class="form-row__col">
                    <v-text-field
                      v-model="form.state"
                      :rules="[rules.required]"
                      label="State"
                      type="text"
                      required
                      dense
                      hide-details="auto"
                      class="line-input custom-filled" />
                  </div>
                </div>
                <v-text-field
                  v-model="form.postalCode"
                  :rules="[rules.required, rules.zip]"
                  validate-on-blur
                  label="Postal Code"
                  type="text"
                  required
                  dense
                  hide-details="auto"
                  class="line-input custom-filled" />
                <v-select
                  v-model="form.referralSource"
                  :items="referralList"
                  item-text="name"
                  item-value="id"
                  label="Where did you hear of us?"
                  dense
                  hide-details="auto"
                  class="line-input custom-filled"
                  :disabled="!clientId" />
                <v-text-field
                  v-if="otherSelected"
                  v-model="form.referralNote"
                  label="Enter your referrer here"
                  required
                  dense
                  hide-details="auto"
                  class="line-input custom-filled" />
                <p v-if="appointmentTypeRequiresNote" class="note-label">The appointment type you've selected requires a reason. Please provide a description below.</p>
                <p v-else class="note-label">Add an additional comment for your appointment?</p>
                <v-text-field
                  v-model="form.additionalNote"
                  :required="appointmentTypeRequiresNote"
                  dense
                  hide-details="auto"
                  class="line-input custom-filled" />
                <div class="button-row">
                  <v-btn class="btn-primary" @click="checkForm()">Submit</v-btn>
                  <v-btn class="btn-secondary" @click="currentStep = 1">Back</v-btn>
                </div>
                <p class="terms-note">Already have an account? <span class="link-text" @click="goToLogin()">Sign in</span></p>
                <p class="terms-note">By creating an account, you agree to the terms presented at the bottom of the page.</p>
              </v-form>
            </div>
          </div>

          <!-- Step 3: Existing patient search -->
          <div v-else-if="currentStep === 3">
            <div class="heading">
              <div class="heading-text">
                <h2>Find your account</h2>
              </div>
            </div>
            <p class="step-description">Please use the information you have on file with {{ clientName }}.</p>
            <div class="step-container">
              <div v-if="searchingForPatient" class="loading-step-overlay">
                <v-progress-circular indeterminate color="primary" size="50" />
              </div>
              <v-form ref="searchForm" class="modern-form" @submit.prevent="searchForPatient">
                <div class="form-row form-row--stack-on-mobile">
                  <div class="form-row__col">
                    <v-text-field
                      v-model="form.firstName"
                      :rules="[rules.required, rules.name]"
                      validate-on-blur
                      label="First Name"
                      type="text"
                      required
                      dense
                      hide-details="auto"
                      class="line-input custom-filled" />
                  </div>
                  <div class="form-row__col">
                    <v-text-field
                      v-model="form.lastName"
                      :rules="[rules.required, rules.name]"
                      validate-on-blur
                      label="Last Name"
                      type="text"
                      required
                      dense
                      hide-details="auto"
                      class="line-input custom-filled" />
                  </div>
                </div>
                <v-text-field
                  v-model="form.birthDate"
                  :rules="[rules.required]"
                  label="Birth Date"
                  type="date"
                  required
                  dense
                  hide-details="auto"
                  class="line-input custom-filled" />
                <v-text-field
                  v-model="form.email"
                  :rules="[rules.required, rules.email]"
                  validate-on-blur
                  label="Email"
                  type="email"
                  required
                  dense
                  hide-details="auto"
                  class="line-input custom-filled" />
                <v-text-field
                  v-model="form.phone"
                  :rules="[rules.required, rules.phone]"
                  validate-on-blur
                  label="Phone Number"
                  type="tel"
                  @keyup="numDash"
                  maxlength="12"
                  required
                  dense
                  hide-details="auto"
                  class="line-input custom-filled" />
                <div class="button-row">
                  <v-btn class="btn-primary" type="submit" :disabled="searchingForPatient">Find Patient</v-btn>
                  <v-btn class="btn-secondary" @click="currentStep = 1">Back</v-btn>
                </div>
              </v-form>
            </div>
          </div>

          <!-- Step 4: Multiple matches -->
          <div v-else-if="currentStep === 4">
            <div class="heading">
              <div class="heading-text">
                <h2>Select your account</h2>
              </div>
            </div>
            <p class="step-description">We found several possible matches.</p>
            <div class="patient-match-container" v-for="(patient, i) in potentialPatientMatches" :key="i">
              <v-card @click="selectPatient(patient)" outlined class="patient-card">
                <h3>{{ patient.firstName }} {{ patient.lastName }}</h3>
                <span>{{ patient.age }}</span>
              </v-card>
            </div>
            <p class="step-description">Don't see your account? Try entering your information again, or call the {{ clientName }} office.</p>
            <v-btn class="btn-secondary full-width-btn" @click="currentStep = 3">Re-enter information</v-btn>
          </div>

          <!-- Step 5: OTP verification -->
          <div v-else-if="currentStep === 5">
            <div class="step-container">
              <div v-if="sendingMultiFactor" class="loading-step-overlay">
                <v-progress-circular indeterminate color="primary" size="50" />
              </div>
              <div v-else-if="restrictedAccount">
                <h2>Account restricted</h2>
                <p class="step-description">The selected account is currently unable to schedule appointments online. Please contact the office to schedule your appointment.</p>
              </div>
              <div v-else>
                <div class="heading">
                  <div class="heading-text">
                    <h2>Verify your identity</h2>
                  </div>
                </div>
                <p class="step-description">Please check your {{ contactMethod }} for a verification code.</p>
                <v-otp-input
                  v-model="otpInput"
                  length="4"
                  type="tel"
                  @finish="completePatientValidation" />
              </div>
            </div>
          </div>

          <!-- Step 6: Book appointment -->
          <div v-else-if="currentStep === 6">
            <div class="heading">
              <div class="heading-text">
                <h2>Almost done!</h2>
              </div>
            </div>
            <p class="step-description">We've found your patient record. You may now create your appointment.</p>
            <p v-if="appointmentTypeRequiresNote" class="note-label">The appointment type you've selected requires a reason for the appointment. Please provide a description below.</p>
            <p v-else class="note-label">Add an additional comment for your appointment?</p>
            <v-text-field
              v-model="form.additionalNote"
              :required="appointmentTypeRequiresNote"
              dense
              hide-details="auto"
              class="line-input custom-filled" />
            <v-btn
              class="btn-primary full-width-btn"
              @click="submitForm"
              :disabled="apptSchedulingLoading">
              Create Appointment
            </v-btn>
          </div>

        </div>
      </div>
    </div>

    <div v-if="clientColor" class="powered-by">
      <span>BROUGHT TO YOU BY</span>
      <img src="@/../public/static/img/acrew-logo-with-effects.png" alt="Logo" />
    </div>

    <!-- Success dialog -->
    <v-dialog v-if="apptSchedulingLoading || appointmentScheduled" persistent v-model="dialog" width="400">
      <v-card class="modern-modal">
        <div v-if="apptSchedulingLoading" class="modal-content" style="text-align:center; padding: 40px;">
          <v-progress-circular indeterminate color="primary" size="50" />
        </div>
        <div v-if="appointmentScheduled" class="modal-content">
          <v-card-title class="modal-title">
            <v-icon color="#10b981" size="24" class="mr-3">mdi-check-circle</v-icon>
            Appointment Scheduled!
          </v-card-title>
          <v-card-text class="modal-content">
            <p>Your appointment has been scheduled successfully!<span v-if="!isAccountLinked"> Follow this link to complete your sign up.</span></p>
          </v-card-text>
          <v-card-actions class="modal-actions">
            <v-btn class="btn-primary" @click="acknowledgeConfirm()">Ok</v-btn>
          </v-card-actions>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import DataService from '../../_services/DataService';
import StateService from '../../_services/StateService';
import ValidateService from '../../_services/ValidateService';

export default {
  data() {
    return {
      currentStep: 1,
      clientId: 0,
      clientLogo: '',
      clientColor: '',
      clientName: '',
      whiteBehindLogo: false,
      searchingForPatient: false,
      sendingMultiFactor: false,
      restrictedAccount: false,
      patientValidationToken: null,
      contactMethod: null,
      otpInput: '',
      potentialPatientMatches: [],
      patientIdToLink: null,
      dialog: false,
      schedulingToken: undefined,
      form: {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        birthDate: '',
        address1: '',
        address2: '',
        city: '',
        state: '',
        postalCode: '',
        referralSource: null,
        referralNote: '',
        additionalNote: '',
      },
      rules: ValidateService.validators,
      errors: [],
      patientId: null,
      isNewPatient: false,
      isAccountLinked: false,
      referralList: [],
      otherSelected: false,
      appointmentTypeRequiresNote: false,
      apptSchedulingLoading: false,
      appointmentScheduled: false,
      states: [],
    };
  },
  computed: {
    logoBackgroundColor() {
      return this.whiteBehindLogo ? 'white' : (this.clientColor || '#11405c');
    },
  },
  watch: {
    ['form.referralSource']() {
      if (!this.form.referralSource) {
        this.otherSelected = false;
      } else {
        const selectedItem = this.referralList.find((item) => item.id === this.form.referralSource);
        this.otherSelected = selectedItem?.name?.match(/\bother\b/i);
      }
    },
  },
  methods: {
    goToLogin() {
      this.$router.push(`/login?clientId=${this.clientId}`);
    },
    async acknowledgeConfirm() {
      this.dialog = false;
      if (!this.isAccountLinked) {
        this.$router.push({
          name: 'PatientSignup',
          query: {
            email: this.form.email,
            firstName: this.form.firstName,
            lastName: this.form.lastName,
            patientId: this.patientId,
          },
        });
      } else {
        this.$router.push('/login');
      }
    },
    async searchForPatient() {
      if (this.$refs.searchForm && !this.$refs.searchForm.validate()) return;
      this.searchingForPatient = true;
      const response = await DataService.searchForPatient(this.clientId, {
        firstName: this.form.firstName,
        lastName: this.form.lastName,
        email: this.form.email,
        phoneNumber: this.form.phone,
        birthDate: this.form.birthDate,
      });

      if (response.status === 200) {
        if (response.data.length === 1) {
          this.currentStep = 5;
          this.patientIdToLink = response.data[0].patientId;
          await this.beginPatientValidation();
        } else if (response.data.length > 0) {
          this.currentStep = 4;
          this.potentialPatientMatches = response.data;
        } else {
          this.$root.$emit('raise_alert', {
            color: 'error',
            text: `We could not find a matching patient record; try again or contact the ${this.clientName} office`,
          });
        }
      } else {
        this.$root.$emit('raise_alert', {
          color: 'error',
          text: response.data ?? 'An error occurred trying to search for a patient; please try again',
        });
      }
      this.searchingForPatient = false;
    },
    async selectPatient(patient) {
      this.currentStep = 5;
      this.patientIdToLink = patient.patientId;
      await this.beginPatientValidation();
    },
    async beginPatientValidation() {
      this.sendingMultiFactor = true;
      const response = await DataService.getPatientValidationToken(this.clientId, this.patientIdToLink);
      if (response.status === 200) {
        this.patientValidationToken = response.data.token;
        this.contactMethod = response.data.sentSms === true ? 'text messages' : 'email';
      } else if (response.status === 403) {
        this.restrictedAccount = true;
      } else {
        this.$root.$emit('raise_alert', { color: 'error', text: 'An error occurred trying to send a validation request' });
      }
      this.sendingMultiFactor = false;
    },
    async completePatientValidation() {
      this.sendingMultiFactor = true;
      const response = await DataService.verifyPatientValidationToken(this.clientId, this.patientIdToLink, this.patientValidationToken, this.otpInput);
      if (response.status === 200) {
        this.schedulingToken = response.data.token;
        this.currentStep = 6;
      } else {
        this.$root.$emit('raise_alert', { color: 'error', text: 'Failed to validate your request' });
      }
      this.sendingMultiFactor = false;
    },
    async submitForm() {
      if (!this.form.additionalNote && this.appointmentTypeRequiresNote) {
        this.$root.$emit('raise_alert', {
          color: 'info',
          text: 'The selected appointment type requires a note be provided stating the reason for the appointment',
        });
        return;
      }

      this.dialog = true;
      this.apptSchedulingLoading = true;

      const body = {
        providerId: this.$route.query.providerId,
        operatoryId: this.$route.query.operatoryId,
        appointmentDate: this.$route.query.appointmentDate,
        appointmentTypeId: this.$route.query.appointmentType,
        email: this.form.email,
        firstName: this.form.firstName,
        middleName: this.form.middleName,
        lastName: this.form.lastName,
        address1: this.form.address1,
        address2: this.form.address2,
        birthDate: this.form.birthDate,
        city: this.form.city,
        state: this.form.state,
        postalCode: this.form.postalCode,
        phone: this.form.phone,
        referralSource: this.form.referralSource,
        referralNote: this.form.referralNote,
        additionalNote: this.form.additionalNote,
      };

      const resp = await DataService.scheduleAppointment(body, this.schedulingToken);
      if (resp.status === 201) {
        this.patientId = resp.data.patientId;
        this.isNewPatient = resp.data.isNewPatient;
        this.isAccountLinked = resp.data.isAccountLinked;
        this.appointmentScheduled = true;
      } else {
        this.$root.$emit('raise_alert', {
          color: 'error',
          text: resp.data || 'Something went wrong. Please try again later.',
        });
        this.dialog = false;
      }
      this.apptSchedulingLoading = false;
    },
    async checkForm() {
      if (this.$refs.newPatientForm && !this.$refs.newPatientForm.validate()) return;
      await this.submitForm();
    },
    numDash(e) {
      var num = this.form.phone;
      if (e.key != 'Backspace' && (num.length === 3 || num.length === 7)) {
        this.form.phone += '-';
      }
    },
    async getClientPreferences() {
      const response = await DataService.getClientPreferences(this.clientId);
      if (response.status === 200) {
        this.clientLogo = response.data.clientLogo;
        this.clientColor = response.data.clientColor;
        this.clientName = response.data.clientName;
        this.whiteBehindLogo = response.data.whiteBehindLogo;
      }
    },
    async getClientReferralMethods() {
      const referralMethods = await DataService.getClientReferralMethods(this.clientId);
      this.referralList = referralMethods.data;
    },
    async getAppointmentType() {
      const appointmentTypeId = this.$route.query.appointmentType;
      if (!appointmentTypeId) {
        this.$root.$emit('raise_alert', {
          color: 'error',
          text: 'No appointment type was selected. Please go back and select an appointment.',
        });
        return;
      }
      const resp = await DataService.getAppointmentType(appointmentTypeId);
      if (resp.status === 200) {
        this.appointmentTypeRequiresNote = resp.data.requireNote;
      }
    },
  },
  async mounted() {
    if (this.$route.query.clientId) {
      DataService.updateClientId(this.$route.query.clientId);
    }
    this.states = StateService.getStates().map((state) => state.name);
    this.clientId = DataService.getClientId();
    await this.getClientPreferences();
    await this.getClientReferralMethods();
    await this.getAppointmentType();
  },
};
</script>

<style scoped lang="scss">
@import '@/scss/variables.scss';
@import '@/scss/auth/page.scss';

.v-stepper__content {
  padding: 0 !important;
}

.step-container {
  position: relative;
  min-height: 200px;

  .loading-step-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    min-height: 200px;
    background: rgba(255, 255, 255, 0.9);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;
  }
}

.form-row {
  display: flex;
  width: 100%;
  gap: 16px;
  margin-bottom: 0;

  &__col {
    flex: 1 1 0;
    min-width: 0;
  }

  &--stack-on-mobile {
    @media (max-width: 600px) {
      flex-direction: column;
      gap: 0;
    }
  }
}

.button-row--stack {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 8px;
}

.full-width-btn {
  width: 100%;
  margin-top: 5px;
}

.note-label {
  font-size: 14px;
  color: #555;
  margin-bottom: 8px;
}

.terms-note {
  margin-top: 16px;
  font-size: 13px;
  color: #666;
  text-align: left;
  line-height: 1.4;
}

.link-text {
  text-decoration: underline;
  cursor: pointer;
  color: #133b54;
}

.patient-match-container {
  margin-bottom: 8px;

  .patient-card {
    width: 100%;
    cursor: pointer;
    border-width: 2px;
    padding: 20px 16px;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;

    &:hover {
      border-color: #b0d35e !important;
      box-shadow: 0 4px 12px rgba(176, 211, 94, 0.2) !important;
    }
  }
}

p.step-description {
  margin-bottom: 16px;
  color: #555;
}
</style>
